import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  BulkUpdateDocument,
  BulkUpdateDocumentsDocumentDocumentsInput,
  BulkUpdateMutation,
} from 'generated/resources/graphql'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import {
  UploadProcessDocument,
  useGetUploadProcessDocuments,
} from 'modules/documents/hooks/useGetUploadProcessDocuments'
import { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'
import employees from 'modules/employees/store/collections/employees'

import BulkModal from './BulkModal'
import { uploadProcessDocumentsToMatches } from './filterDocuments'

interface Props {
  uploadProcess: UploadProcess
  documents: Document[]
  onFinished?: () => void
}

const BulkAssigner = ({ uploadProcess, documents, onFinished }: Props) => {
  const [isFetchingScores, setIsFetchingScores] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [selectedIdsForEsignature, setSelectedIdsForEsignature] = React.useState<number[]>([])
  const [uploadProcessDocuments, setUploadProcessDocuments] = React.useState<
    UploadProcessDocument[]
  >([])
  const [bulkUpdate] = useMutation(BulkUpdateDocument, {
    extractorFn: (data: BulkUpdateMutation) => data.documentsMutations.bulkUpdateDocumentsDocument,
  })
  const requestEsignatureForm = React.useRef(
    new AssignEsignatureForm({
      requestEsignature: false,
    })
  ).current

  const { fetch, loading: loadingUploadProcessDocuments } = useGetUploadProcessDocuments()

  useEffect(() => {
    setIsFetchingScores(loadingUploadProcessDocuments)
  }, [loadingUploadProcessDocuments])

  useUnsafeEffect(() => {
    setSelectedIdsForEsignature(uploadProcess.additionalSigners.map((signer) => signer.id))
  }, [uploadProcess.additionalSigners])

  const onClickOpenModal = () => {
    setIsModalOpen(true)

    fetchUploadProcessDocuments()
  }

  const onClose = () => {
    setIsModalOpen(false)
    setIsFetchingScores(true)
  }

  const onSelectIdsForEsignature = (selectedIdsForEsignature: number[]) => {
    setSelectedIdsForEsignature(selectedIdsForEsignature)
  }

  const uploadProcessDocumentChunks = (documentIds: number[]): number[][] => {
    const chunks = []
    const chunkSize = 100

    for (let i = 0; i < documentIds.length; i += chunkSize) {
      chunks.push(documentIds.slice(i, i + chunkSize))
    }

    return chunks
  }

  const fetchUploadProcessDocuments = async () => {
    setIsFetchingScores(true)

    const documentsChunk = uploadProcessDocumentChunks(documents.map((doc) => doc.id))

    const result = await Promise.all(
      documentsChunk.map((documentIds) =>
        fetch({
          documentIds: documentIds,
        })
      )
    )

    setUploadProcessDocuments(result.flat())

    setIsFetchingScores(false)
  }

  const processScores = () => {
    const ids = uploadProcessDocuments.map((doc) => doc.documentId)

    const matches = uploadProcessDocumentsToMatches(ids, uploadProcessDocuments)

    // NOTE: we're only interested in perfect matches,
    // we discard documents with more than one match
    const documentSingleMatches = Object.keys(matches).filter((key) => matches[key]?.length === 1)

    const countMatches = documentSingleMatches.length
    const remainingDocs = documents.length - countMatches

    return [countMatches, remainingDocs, documentSingleMatches, matches]
  }

  const [countMatches, remainingDocs, documentSingleMatches, documentMatches] = processScores()

  const assignDocuments = async () => {
    setIsLoading(true)

    const response = await bulkUpdate({
      variables: { documents: docsToUpdate() },
    })
    setIsLoading(false)

    const result = handleResult(response, () => undefined)

    if (result.success) {
      onFinished && onFinished()
      setIsModalOpen(false)
    }
  }

  const docsToUpdate = () => {
    const requestEsignature = uploadProcess.esignatureRequest ?? false

    return (documentSingleMatches as string[]).map((docId) => {
      const employeeId = (documentMatches as any)[docId][0]

      const employee = employees.find({
        id: employeeId,
      })

      if (!employee || !employee.attributes.access_id) {
        throw new Error("Can't assign document to null employee or without access")
      }

      const signees = [employee.attributes.access_id, ...selectedIdsForEsignature]
      const uniqueSignees = Array.from(new Set(signees))

      const options: BulkUpdateDocumentsDocumentDocumentsInput = {
        documentId: parseInt(docId),
        employeeId: parseInt(employeeId),
        signees: uniqueSignees,
        requestEsignature: requestEsignature,
        public: true,
        isPendingAssignment: false,
        distributionMethod: 'bulk_match',
      }

      return options
    })
  }

  return (
    <>
      <Button type="secondary" onClick={onClickOpenModal} iconName={'check-circle-line'}>
        {i18n.t('split_documents.header.auto_send_v2')}
      </Button>
      {isModalOpen && (
        <BulkModal
          selectedIdsForEsignature={selectedIdsForEsignature}
          requestEsignatureForm={requestEsignatureForm}
          onSelectIdsForEsignature={onSelectIdsForEsignature}
          onClose={onClose}
          onClick={assignDocuments}
          isLoading={isLoading}
          isFetchingScores={isFetchingScores}
          countMatches={countMatches as number}
          remainingDocs={remainingDocs as number}
          uploadProcess={uploadProcess}
        />
      )}
    </>
  )
}

export default BulkAssigner
